import { useState, useEffect, useRef } from "react";
import svgPaths from "../../assets/new_ui_imports/Solutions/svg-5r19r3a1v6.js";
import imgCharging from "../../assets/new_ui_imports/Solutions/4a2066229a02a21b3b547cd6bbdb793dd44749e0.png";
import imgServicing from "../../assets/new_ui_imports/Solutions/cfbaff722151f85851138832de29f0ca76a200ed.png";
import imgHeroBg from "../../assets/new_ui_img/solutions_banner.jpg";
import ParkingIllustration from "./ParkingIllustration";
import { Header, Footer, ScrollToTop } from "../landingPageNewUI/components/NewHeader";

// --- Scroll reveal -------------------------------------------------------------
function useReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("visible"); }),
      { threshold: 0.07, rootMargin: "0px 0px -40px 0px" }
    );
    document.querySelectorAll(".sol-reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

const TAB_META = {
  parking:   { label: "PARKING",   badge: "bg-[rgba(10,102,194,0.5)]" },
  charging:  { label: "CHARGING",  badge: "bg-[rgba(67,214,81,0.5)]"  },
  servicing: { label: "SERVICING", badge: "bg-[#c0c0c0]"              },
};

function Chevron({ open }) {
  return (
    <div className={`flex-shrink-0 transition-transform duration-300 ${open ? "-scale-y-100" : ""}`}>
      <div className="relative size-[36px]">
        <svg className="absolute block inset-0 size-full" fill="none" height="36" preserveAspectRatio="none" viewBox="0 0 36 36" width="36">
          <path d={svgPaths.p3de18b00} stroke="#333333" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" />
        </svg>
      </div>
    </div>
  );
}

function ArrowRight() {
  return (
    <div className="h-0 relative shrink-0 w-[18px]">
      <div className="absolute inset-[-7.36px_-5.56%]">
        <svg className="block size-full" fill="none" height="14.7279" preserveAspectRatio="none" viewBox="0 0 20 14.7279" width="20">
          <path d={svgPaths.pd274400} fill="#121212" />
        </svg>
      </div>
    </div>
  );
}

function ContactLink() {
  return (
    <div className="flex flex-col items-start">
      <div className="flex gap-[12px] items-center">
        <p className="font-['Google_Sans_Flex:SemiBold',sans-serif] font-semibold leading-[1.3] not-italic text-[#121212] text-[clamp(1rem,1.3vw,1.25rem)] whitespace-nowrap"
          style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
          Contact us to learn more
        </p>
        <ArrowRight />
      </div>
    </div>
  );
}

function ParkingBody() {
  return (
    <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-10 py-[80px]">
      <div className="flex flex-[1_0_0] flex-col gap-[44px] items-start min-w-0">
        <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium leading-[1.3] not-italic text-[clamp(1.6rem,2.8vw,2.5rem)] text-black w-full"
          style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
          Best-in-Class Parking Platform
        </p>
        <div className="flex flex-col items-start justify-center w-full">
          <p className="font-['Lato:Regular',sans-serif] leading-[1.3] not-italic text-[#6b6b6b] text-[clamp(1rem,1.3vw,1.25rem)] w-full">
            GeoParking enables property owners to earn from underutilized parking spaces by make them available to autonomous vehicle fleets on flexible terms.
          </p>
        </div>
        <div className="flex flex-col gap-[24px] items-start not-italic text-[clamp(1rem,1.3vw,1.25rem)] w-full">
          <p className="font-['Google_Sans_Flex:Bold',sans-serif] font-bold leading-[normal] text-[#121212] w-full"
            style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
            Key Features:
          </p>
          <ul className="block font-['Lato:Regular',sans-serif] list-disc text-[#6b6b6b] w-full">
            <li className="mb-2">
              <span className="leading-[1.3]">Designate the exact parking spaces available for robotaxis, along with preferred pick-up and drop-off locations, to support safe, efficient, and organized operations within your parking lot.</span>
            </li>
            <li className="mb-2">
              <span className="leading-[1.3]">Guaranteed space availability is not required. Robotaxis dynamically reroute to alternative GeoParking locations when spaces are occupied. No additional signage is required for robotaxi operations.</span>
            </li>
            <li className="mb-2">
              <span className="leading-[1.3]">The secure GeoParking dashboard provides 24/7 access to view revenue, manage available parking spaces, and update your unavailability calendar as your property's needs change.</span>
            </li>
            <li className="mb-2">
              <span className="leading-[1.3]">Cancel anytime.</span>
            </li>
          </ul>
        </div>
      </div>
      <div className="w-full lg:w-[55%] flex-shrink-0 overflow-hidden rounded-[10px]">
        <div className="w-full max-w-full overflow-x-auto">
          <div style={{ minWidth: '420px' }}>
            <ParkingIllustration />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChargingBody() {
  return (
    <div className="flex flex-col-reverse lg:flex-row items-start gap-8 lg:gap-10 py-[30px]">
      <div className="w-full lg:w-[55%] flex-shrink-0 rounded-[10px] overflow-hidden">
        <img alt="" className="w-full h-[300px] sm:h-[400px] lg:h-[607px] object-cover mix-blend-multiply" src={imgCharging} />
      </div>
      <div className="flex flex-[1_0_0] flex-col gap-[44px] items-start min-w-0 py-[80px]">
        <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium leading-[1.3] not-italic text-[clamp(1.6rem,2.8vw,2.5rem)] text-black w-full"
          style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
          AV Charging Coming Soon
        </p>
        <div className="flex flex-col font-['Lato:Regular',sans-serif] gap-[24px] items-start leading-[1.3] not-italic text-[#6b6b6b] text-[clamp(1rem,1.3vw,1.25rem)] w-full">
          <p className="w-full">Proposals for autonomous vehicle charging solutions are currently in development.</p>
          <p className="w-full">If you own or manage a parking lot that may be a strong candidate for future AV charging infrastructure, we'd love to hear from you.</p>
        </div>
        <ContactLink />
      </div>
    </div>
  );
}

function ServicingBody() {
  return (
    <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-10 py-[30px]">
      <div className="flex flex-[1_0_0] flex-col gap-[44px] items-start min-w-0 py-[80px]">
        <p className="font-['Google_Sans_Flex:Medium',sans-serif] font-medium leading-[1.3] not-italic text-[clamp(1.6rem,2.8vw,2.5rem)] text-black w-full"
          style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
          AV Servicing Opportunities
        </p>
        <div className="flex flex-col font-['Lato:Regular',sans-serif] gap-[24px] items-start leading-[1.3] not-italic text-[#6b6b6b] text-[clamp(1rem,1.3vw,1.25rem)] w-full">
          <p className="w-full">Proposals for autonomous vehicle servicing solutions are currently in development.</p>
          <p className="w-full">If you own or manage a parking location that may be a strong candidate for supporting autonomous vehicle fleet operations, we'd love to hear from you.</p>
        </div>
        <ContactLink />
      </div>
      <div className="w-full lg:w-[55%] flex-shrink-0 rounded-[10px] overflow-hidden">
        <img alt="" className="w-full h-[300px] sm:h-[400px] lg:h-[679px] object-cover mix-blend-multiply" src={imgServicing} />
      </div>
    </div>
  );
}

const BODY = {
  parking:   <ParkingBody />,
  charging:  <ChargingBody />,
  servicing: <ServicingBody />,
};

function AccordionItem({ tabKey, open, onToggle }) {
  const { label, badge } = TAB_META[tabKey];
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  useEffect(() => {
    const el = bodyRef.current;
    if (!el) return;
    setHeight(open ? el.scrollHeight : 0);
  }, [open]);

  return (
    <div className="border-b border-[#cecece]">
      <button
        className="bg-transparent border-0 outline-none appearance-none w-full flex gap-[10px] items-center py-[8px] text-left cursor-pointer group"
        onClick={onToggle}
        aria-expanded={open}
      >
        <div className="flex flex-[1_0_0] items-center min-w-0">
          <div className={`${badge} flex items-center justify-center px-[16px] py-[8px] rounded-[8px] flex-shrink-0 transition-opacity group-hover:opacity-90`}>
            <p className="font-['Google_Sans_Flex:Bold',sans-serif] font-bold leading-[1.3] not-italic text-[#333] text-[18px] whitespace-nowrap"
              style={{ fontVariationSettings: '"GRAD" 0, "ROND" 0, "wdth" 100' }}>
              {label}
            </p>
          </div>
        </div>
        <Chevron open={open} />
      </button>
      <div
        className="overflow-hidden transition-[height] duration-500 ease-in-out"
        style={{ height }}
        aria-hidden={!open}
      >
        <div ref={bodyRef}>{BODY[tabKey]}</div>
      </div>
    </div>
  );
}

export default function SolutionsPage() {
  const [openTab, setOpenTab] = useState("parking");
  useReveal();

  return (
    <div className="bg-[#f5f5f5] min-h-screen">
      <Header />
      <main>
        {/* Hero banner */}
        <div className="pt-20">
          <div className="relative w-full overflow-hidden" style={{ height: "140px" }}>
            <div className="absolute inset-0 hero-bg-animate" style={{ transformOrigin: "center top" }}>
              <img alt="" className="absolute inset-0 max-w-none pointer-events-none size-full object-cover" src={imgHeroBg} />
            </div>
            <div className="absolute inset-0 bg-[rgba(255,255,255,0.8)]" />
            <div className="relative z-10 max-w-[1400px] mx-auto px-6 h-full flex items-center">
              <p className="hero-title-animate font-['Montserrat:Bold',sans-serif] font-bold leading-[1.3] text-[clamp(1.4rem,3.2vw,2.75rem)] text-black">
                Smarter Parking - Better Outcomes
              </p>
            </div>
          </div>
        </div>

        {/* Accordion section */}
        <section className="bg-[#f5f5f5] py-[60px]">
          <div className="max-w-[1400px] mx-auto px-6">
            <div className="flex flex-col">
              {["parking", "charging", "servicing"].map((key, i) => (
                <div key={key} className="sol-reveal" style={{ transitionDelay: `${i * 60}ms` }}>
                  <AccordionItem
                    tabKey={key}
                    open={openTab === key}
                    onToggle={() => setOpenTab(key)}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <ScrollToTop />
    </div>
  );
}
